/* eslint-disable react/jsx-props-no-spreading */
import React, { ReactNode, CSSProperties } from 'react';
import { Modal } from 'antd';
import type { ModalProps } from 'antd';
import { useLanguageStore } from '$/store/languageStore';
import classNames from 'classnames';
import styles from './index.module.scss';
import Button from '../Button';

export default ModalComponent;
type ModalComponentProps = {
  isModalOpen: boolean;
  children: ReactNode;
  title?: string;
  type?: 'question' | 'warning';
  close?: () => void;
  cancel?: () => void;
  confirm?: () => void;
  headerStyle?: CSSProperties;
  modalClassName?: string;
  cancelText?: string;
  confirmText?: string;
  modalContentMouseDown?: React.MouseEventHandler<HTMLDivElement>;
} & Omit<ModalProps, 'open' | 'onCancel' | 'modalRender' | 'children' | 'title'>;

/**
 * 统一封装业务弹窗并透传 antd Modal 原生属性。
 */
function ModalComponent({
  isModalOpen,
  close,
  cancel,
  confirm,
  cancelText,
  confirmText,
  headerStyle,
  maskStyle,
  modalClassName,
  width,
  children,
  title,
  type,
  modalContentMouseDown,
  mask = true,
  centered = true,
  zIndex = 500,
  ...antdModalProps
}: ModalComponentProps) {
  const languageStore = useLanguageStore((state) => state.languageStore);
  const renderModalContent = () => (
    <div
      className={classNames(styles.modal, modalClassName)}
      onMouseDown={modalContentMouseDown}
    >
      {!!close && (
      <div
        className={styles.header}
        style={{
          ...headerStyle,
        }}
      >
        <div className={styles.title}>{title || ''}</div>
        <div
          className={styles.close}
          onClick={() => {
            close?.();
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" clipRule="evenodd" d="M3.01359 3.01335C3.42039 2.60655 4.07994 2.60655 4.48673 3.01335L10.0002 8.52678L15.5136 3.01335C15.9204 2.60655 16.5799 2.60655 16.9867 3.01335C17.3935 3.42015 17.3935 4.07969 16.9867 4.48649L11.4733 9.99992L16.9867 15.5133C17.3935 15.9201 17.3935 16.5797 16.9867 16.9865C16.5799 17.3933 15.9204 17.3933 15.5136 16.9865L10.0002 11.4731L4.48673 16.9865C4.07994 17.3933 3.42039 17.3933 3.01359 16.9865C2.6068 16.5797 2.6068 15.9201 3.01359 15.5133L8.52702 9.99992L3.01359 4.48649C2.6068 4.07969 2.6068 3.42015 3.01359 3.01335Z" />
          </svg>
        </div>
      </div>
      )}

      <div className={styles.centerBox}>
        {type === 'question' && (
        <svg style={{ marginLeft: '12px' }} xmlns="http://www.w3.org/2000/svg" width="60" height="60" viewBox="0 0 60 60" fill="currentColor">
          <path d="M30.628 20.3159C28.3916 20.2368 26.5397 21.4396 26.2269 23.4142C26.0649 24.437 25.1044 25.1348 24.0816 24.9728C23.0588 24.8108 22.361 23.8503 22.5231 22.8275C23.2281 18.3763 27.3137 16.4464 30.7604 16.5682C32.5266 16.6306 34.3462 17.2116 35.7579 18.4216C37.211 19.6672 38.125 21.4942 38.125 23.7635C38.125 26.9624 35.8456 28.931 34.3197 30.2489L34.3183 30.25C34.2105 30.3432 34.1064 30.4331 34.0071 30.52C32.2429 32.0639 31.4679 32.9683 31.6624 34.4421C31.7979 35.4688 31.0755 36.4109 30.0489 36.5464C29.0223 36.6819 28.0802 35.9595 27.9447 34.9329C27.4606 31.266 29.9088 29.1234 31.5375 27.698C33.427 26.0444 34.375 25.1676 34.375 23.7635C34.375 22.5788 33.9318 21.7954 33.3174 21.2688C32.6616 20.7067 31.7033 20.3539 30.628 20.3159Z" />
          <path d="M30 45.3125C31.3807 45.3125 32.5 44.1932 32.5 42.8125C32.5 41.4318 31.3807 40.3125 30 40.3125C28.6193 40.3125 27.5 41.4318 27.5 42.8125C27.5 44.1932 28.6193 45.3125 30 45.3125Z" />
          <path d="M3.125 30.3125C3.125 15.4698 15.1573 3.4375 30 3.4375C44.8427 3.4375 56.875 15.4698 56.875 30.3125C56.875 45.1552 44.8427 57.1875 30 57.1875C15.1573 57.1875 3.125 45.1552 3.125 30.3125ZM30 7.1875C17.2284 7.1875 6.875 17.5409 6.875 30.3125C6.875 43.0841 17.2284 53.4375 30 53.4375C42.7716 53.4375 53.125 43.0841 53.125 30.3125C53.125 17.5409 42.7716 7.1875 30 7.1875Z" />
        </svg>
        )}

        {type === 'warning' && (
        <svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" viewBox="0 0 60 60" fill="currentColor">
          <path d="M32.4576 31.0344L31.2489 39.537L31.2452 39.5591C31.2319 39.6392 31.2202 39.7151 31.2101 39.7869C31.3479 39.7257 31.4982 39.6546 31.6615 39.5729C32.5877 39.1098 33.714 39.4853 34.1771 40.4115C34.6402 41.3377 34.2648 42.4639 33.3386 42.927C32.6555 43.2686 31.9609 43.5614 31.2998 43.7125C30.6739 43.8556 29.7669 43.9438 28.9118 43.4718C27.972 42.9531 27.5954 42.049 27.4693 41.2953C27.3491 40.5769 27.409 39.7741 27.5424 38.9656L28.7512 30.463L28.7549 30.4408C28.7682 30.3608 28.7799 30.2849 28.79 30.213C28.6521 30.2743 28.5019 30.3454 28.3386 30.427C27.4123 30.8902 26.2861 30.5147 25.823 29.5885C25.3599 28.6623 25.7353 27.536 26.6615 27.0729C27.3445 26.7314 28.0391 26.4386 28.7002 26.2875C29.3261 26.1444 30.2331 26.0562 31.0883 26.5282C32.028 27.0469 32.4047 27.951 32.5307 28.7046C32.6509 29.4231 32.591 30.2259 32.4576 31.0344Z" />
          <path d="M30 16.875C28.6193 16.875 27.5 17.9943 27.5 19.375C27.5 20.7557 28.6193 21.875 30 21.875C31.3807 21.875 32.5 20.7557 32.5 19.375C32.5 17.9943 31.3807 16.875 30 16.875Z" />
          <path d="M3.125 30C3.125 15.1573 15.1573 3.125 30 3.125C44.8427 3.125 56.875 15.1573 56.875 30C56.875 44.8427 44.8427 56.875 30 56.875C15.1573 56.875 3.125 44.8427 3.125 30ZM30 6.875C17.2284 6.875 6.875 17.2284 6.875 30C6.875 42.7716 17.2284 53.125 30 53.125C42.7716 53.125 53.125 42.7716 53.125 30C53.125 17.2284 42.7716 6.875 30 6.875Z" />
        </svg>
        )}
        {children}
      </div>
      {cancel || confirm ? (
        <div className={styles.bottomBox}>
          {cancel ? (
            <Button
              type="plain"
              onClick={() => {
                cancel();
              }}
              size="large"
              className={`${styles.button} ${styles.cancel}`}
            >
              {cancelText || languageStore?.smartcatTranslationText((v) => v.RoboNeo_Web_toast_cancel)}
            </Button>
          ) : null}
          {confirm ? (
            <Button
              onClick={() => {
                confirm();
              }}
              size="large"
              type="primary"
              className={styles.button}
            >
              {confirmText || languageStore?.smartcatTranslationText((v) => v.RoboNeo_Web_toast_confirm)}
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
  return (
    <Modal
      {...antdModalProps}
      open={isModalOpen}
      onCancel={close}
      centered={centered}
      mask={mask}
      closable={!!close}
      width={width}
      zIndex={zIndex}
      maskStyle={maskStyle}
      modalRender={renderModalContent}
    />
  );
}
